/**
 * Multi-language Route and SEO Configuration for Fanø Chokolade
 * Supported languages:
 *  - 'da': Dansk (Primary default language, root paths)
 *  - 'en': English (Prefixed with /en)
 *  - 'de': Deutsch (Prefixed with /de)
 */

export const SITE_URL = 'https://fanoechokolade.dk'

export const LANGUAGES = {
  da: {
    code: 'da',
    name: 'Dansk',
    locale: 'da_DK',
    prefix: '',
    isDefault: true,
    flag: '🇩🇰',
  },
  en: {
    code: 'en',
    name: 'English',
    locale: 'en_GB',
    prefix: '/en',
    isDefault: false,
    flag: '🇬🇧',
  },
  de: {
    code: 'de',
    name: 'Deutsch',
    locale: 'de_DE',
    prefix: '/de',
    isDefault: false,
    flag: '🇩🇪',
  },
}

export const DEFAULT_LANGUAGE = 'da'

/**
 * Route Registry
 * Maps each logical page ID to localized paths across all 3 languages,
 * along with language-specific default SEO titles and meta descriptions.
 */
export const PAGE_ROUTES = {
  home: {
    id: 'home',
    paths: {
      da: '/',
      en: '/en',
      de: '/de',
    },
    seo: {
      da: {
        title: 'Forside - Fanø Chokolade | Håndlavet chokolade og café på Fanø',
        description:
          'Håndlavet chokolade og café på Fanø. Besøg Fanø Chokolade ved Fanø Bad – smag, hygge og kvalitet. Hyggestemning i café eller hjemme.',
      },
      en: {
        title: 'Handcrafted Chocolate & Café Fanø - Fanø Chokolade',
        description:
          'Artisanal handcrafted chocolate and cozy café on Fanø island. Visit Fanø Chokolade by Fanø Bad – exquisite taste, passion and quality craftsmanship.',
      },
      de: {
        title: 'Handgemachte Schokolade & Café auf Fanø - Fanø Chokolade',
        description:
          'Handgemachte Schokolade und gemütliches Café auf der dänischen Insel Fanø. Besuchen Sie Fanø Chokolade bei Fanø Bad – echter Geschmack und Qualität.',
      },
    },
  },
  webshop: {
    id: 'webshop',
    paths: {
      da: '/webshop',
      en: '/en/shop',
      de: '/de/shop',
    },
    seo: {
      da: {
        title: 'Webshop - Fanø Chokolade',
        description:
          'Bestil et håndplukket udvalg af håndlavet chokolade online – eller send en forespørgsel på din egen kage til fest og særlige lejligheder.',
      },
      en: {
        title: 'Online Shop - Fanø Chokolade',
        description:
          'Order a handpicked selection of handmade chocolates online, or send an inquiry for custom celebration cakes on Fanø.',
      },
      de: {
        title: 'Onlineshop - Fanø Chokolade',
        description:
          'Bestellen Sie eine handverlesene Auswahl handgemachter Pralinen und Schokolade online – oder fragen Sie Ihre Wunschtorte an.',
      },
    },
  },
  retailers: {
    id: 'retailers',
    paths: {
      da: '/forhandlerliste',
      en: '/en/retailers',
      de: '/de/haendler',
    },
    seo: {
      da: {
        title: 'Forhandlerliste - Fanø Chokolade',
        description:
          'Se hvor du kan købe Fanø Chokolades produkter i udvalgte butikker, kroer og specialforretninger i Danmark.',
      },
      en: {
        title: 'Retailers & Stockists - Fanø Chokolade',
        description:
          'Find where to buy Fanø Chokolade products at select specialty shops, inns and retailers across Denmark.',
      },
      de: {
        title: 'Händler & Verkaufsstellen - Fanø Chokolade',
        description:
          'Übersicht über Verkaufsstellen und Händler von Fanø Chokolade in ausgewählten Fachgeschäften und Gasthöfen.',
      },
    },
  },
  contact: {
    id: 'contact',
    paths: {
      da: '/aabningstider-og-adresse',
      en: '/en/opening-hours-and-location',
      de: '/de/oeffnungszeiten-und-adresse',
    },
    seo: {
      da: {
        title: 'Kontakt & Åbningstider - Fanø Chokolade',
        description:
          'Besøg caféen på Strandvejen 58, Fanø Bad. Se åbningstider, adresse og kontakt Fanø Chokolade her. Book bord eller ring direkte.',
      },
      en: {
        title: 'Contact & Opening Hours - Fanø Chokolade',
        description:
          'Visit our café and boutique at Strandvejen 58, Fanø Bad. See opening hours, address and contact information for Fanø Chokolade.',
      },
      de: {
        title: 'Kontakt & Öffnungszeiten - Fanø Chokolade',
        description:
          'Besuchen Sie unser Café und unsere Manufaktur am Strandvejen 58, Fanø Bad. Öffnungszeiten, Anfahrt und Kontaktdaten.',
      },
    },
  },
  about: {
    id: 'about',
    paths: {
      da: '/om-mig',
      en: '/en/about-me',
      de: '/de/ueber-mich',
    },
    seo: {
      da: {
        title: 'Om mig - Fanø Chokolade',
        description:
          'Mød Karina, kvinden bag Fanø Chokolade. Fra hobby til café – passion for håndlavet chokolade og kager med online butik og café på Fanø.',
      },
      en: {
        title: 'About Me - Fanø Chokolade',
        description:
          'Meet Karina, the artisan chocolatier behind Fanø Chokolade. From passion project to beloved café and handcrafted chocolate studio on Fanø.',
      },
      de: {
        title: 'Über mich - Fanø Chokolade',
        description:
          'Lernen Sie Karina kennen, die Chocolatière hinter Fanø Chokolade. Leidenschaft für handgemachte Schokolade und feine Torten auf Fanø.',
      },
    },
  },
  eu_lag: {
    id: 'eu_lag',
    paths: {
      da: '/om-mig',
      en: '/en/eu-lag-support',
      de: '/de/eu-lag-foerderung',
    },
    seo: {
      da: {
        title: 'LAG Understøttelse - Fanø Chokolade',
        description:
          'LAG Understøttelse - Fanø Chokolade. Læs om EU-medfinansiering til chokoladetempereringsmaskine og udvidelse af produktionen.',
      },
      en: {
        title: 'EU LAG Support - Fanø Chokolade',
        description:
          'Information on EU LAG co-funding for new chocolate tempering machinery and production development at Fanø Chokolade.',
      },
      de: {
        title: 'EU-LAG-Förderung - Fanø Chokolade',
        description:
          'Informationen zur Kofinanzierung durch den Europäischen Landwirtschaftsfonds (LAG) für Fanø Chokolade.',
      },
    },
  },
  cake_inquiry: {
    id: 'cake_inquiry',
    paths: {
      da: '/forspoergsel',
      en: '/en/cake-inquiry',
      de: '/de/tortenanfrage',
    },
    seo: {
      da: {
        title: 'Kageforespørgsel - Fanø Chokolade',
        description:
          'Bestil en unik kage til bryllup, fødselsdag eller fest, privat eller til virksomheden. Fanø Chokolade laver kager helt efter dit ønske.',
      },
      en: {
        title: 'Custom Cake Inquiry - Fanø Chokolade',
        description:
          'Request a personalized quote for your custom wedding or celebration cake from Fanø Chokolade. Fast reply within 24 hours.',
      },
      de: {
        title: 'Tortenanfrage - Fanø Chokolade',
        description:
          'Fragen Sie Ihre Traumtorte für Hochzeit oder Fest unverbindlich bei Fanø Chokolade an. Wir antworten werktags innerhalb von 24 Stunden.',
      },
    },
  },
  terms: {
    id: 'terms',
    paths: {
      da: '/betingelser',
      en: '/en/terms',
      de: '/de/agb',
    },
    seo: {
      da: {
        title: 'Betingelser - Fanø Chokolade',
        description:
          'Betingelser for Fanø Chokolade. Læs om håndtering af data, henvendelser og bestillinger.',
      },
      en: {
        title: 'Terms & Privacy - Fanø Chokolade',
        description:
          'Terms and privacy policies of Fanø Chokolade. Safe data handling and customer guidelines.',
      },
      de: {
        title: 'AGB & Datenschutz - Fanø Chokolade',
        description:
          'Allgemeine Geschäftsbedingungen und Datenschutzrichtlinien von Fanø Chokolade.',
      },
    },
  },
  forbidden: {
    id: 'forbidden',
    paths: {
      da: '/403',
      en: '/en/403',
      de: '/de/403',
    },
    seo: {
      da: {
        title: '403 Adgang nægtet - Fanø Chokolade',
        description: 'Du har ikke adgang til denne ressource.',
      },
      en: {
        title: '403 Forbidden - Fanø Chokolade',
        description: 'You do not have permission to access this resource.',
      },
      de: {
        title: '403 Zugriff verweigert - Fanø Chokolade',
        description: 'Sie haben keine Berechtigung für diese Ressource.',
      },
    },
  },
}

/**
 * 301 Permanent Redirects Configuration
 * Maps legacy/alternative URLs to their canonical, language-specific target URL.
 */
export const REDIRECTS = [
  // Legacy Danish URLs
  { from: '/om-mig-2', to: '/om-mig' },
  { from: '/lag-understoettelse', to: '/om-mig#projektstoette' },
  { from: '/kontakt', to: '/aabningstider-og-adresse' },
  { from: '/find-mig', to: '/aabningstider-og-adresse' },
  { from: '/produkter', to: '/webshop' },
  { from: '/shop', to: '/webshop' },

  // English aliases / alternative slugs -> canonical English URLs
  { from: '/en/contact', to: '/en/opening-hours-and-location' },
  { from: '/en/find-us', to: '/en/opening-hours-and-location' },
  { from: '/en/aabningstider-og-adresse', to: '/en/opening-hours-and-location' },
  { from: '/en/om-mig', to: '/en/about-me' },
  { from: '/en/om-mig-2', to: '/en/about-me' },
  { from: '/en/about', to: '/en/about-me' },
  { from: '/en/forspoergsel', to: '/en/cake-inquiry' },
  { from: '/en/inquiry', to: '/en/cake-inquiry' },
  { from: '/en/webshop', to: '/en/shop' },
  { from: '/en/forhandlerliste', to: '/en/retailers' },
  { from: '/en/stockists', to: '/en/retailers' },
  { from: '/en/lag-understoettelse', to: '/en/eu-lag-support' },
  { from: '/en/betingelser', to: '/en/terms' },

  // German aliases / alternative slugs -> canonical German URLs
  { from: '/de/kontakt', to: '/de/oeffnungszeiten-und-adresse' },
  { from: '/de/find-us', to: '/de/oeffnungszeiten-und-adresse' },
  { from: '/de/aabningstider-og-adresse', to: '/de/oeffnungszeiten-und-adresse' },
  { from: '/de/om-mig', to: '/de/ueber-mich' },
  { from: '/de/om-mig-2', to: '/de/ueber-mich' },
  { from: '/de/about-me', to: '/de/ueber-mich' },
  { from: '/de/forspoergsel', to: '/de/tortenanfrage' },
  { from: '/de/cake-inquiry', to: '/de/tortenanfrage' },
  { from: '/de/anfrage', to: '/de/tortenanfrage' },
  { from: '/de/webshop', to: '/de/shop' },
  { from: '/de/forhandlerliste', to: '/de/haendler' },
  { from: '/de/retailers', to: '/de/haendler' },
  { from: '/de/lag-understoettelse', to: '/de/eu-lag-foerderung' },
  { from: '/de/eu-lag-support', to: '/de/eu-lag-foerderung' },
  { from: '/de/betingelser', to: '/de/agb' },
  { from: '/de/terms', to: '/de/agb' },
]

/**
 * Detect language based on the URL pathname
 */
export function getLanguageFromPath(pathname = '/') {
  const cleanPath = pathname.split('?')[0].split('#')[0]
  if (cleanPath === '/en' || cleanPath.startsWith('/en/')) {
    return 'en'
  }
  if (cleanPath === '/de' || cleanPath.startsWith('/de/')) {
    return 'de'
  }
  return 'da'
}

/**
 * Resolve logical page ID based on pathname
 */
export function getPageIdFromPath(pathname = '/') {
  const cleanPath = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'

  // Exact matching against PAGE_ROUTES
  for (const [pageId, entry] of Object.entries(PAGE_ROUTES)) {
    for (const [lang, path] of Object.entries(entry.paths)) {
      const normalizedPath = path.replace(/\/+$/, '') || '/'
      if (normalizedPath === cleanPath) {
        return pageId
      }
    }
  }

  // Check redirects
  for (const redir of REDIRECTS) {
    if (redir.from === cleanPath) {
      return getPageIdFromPath(redir.to)
    }
  }

  return null
}

/**
 * Get the target localized URL for a page in a specific language
 */
export function getLocalizedPath(pageId, targetLang = 'da') {
  const page = PAGE_ROUTES[pageId]
  if (page && page.paths[targetLang]) {
    return page.paths[targetLang]
  }
  // Fallback to home of that language
  return targetLang === 'da' ? '/' : `/${targetLang}`
}

/**
 * Get alternate hreflang URLs for a given pageId or pathname
 */
export function getHreflangAlternates(pageId, currentPath = '/') {
  const resolvedId = pageId || getPageIdFromPath(currentPath)
  if (resolvedId && PAGE_ROUTES[resolvedId]) {
    const paths = PAGE_ROUTES[resolvedId].paths
    return {
      da: `${SITE_URL}${paths.da}`,
      en: `${SITE_URL}${paths.en}`,
      de: `${SITE_URL}${paths.de}`,
      'x-default': `${SITE_URL}${paths.da}`, // Danish is default
    }
  }

  // Generic fallback if not matched
  return {
    da: `${SITE_URL}/`,
    en: `${SITE_URL}/en`,
    de: `${SITE_URL}/de`,
    'x-default': `${SITE_URL}/`,
  }
}
